import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static outlets = ["company-admin--customer-search--index"]
  static targets = [
    "customTemplateSelect",
    "deliveryBasisRadio",
    "leaseCount",
    "leaseCustomerCount",
    "recipient",
    "selectedCustomersLoadingIndicator",
    "selectedCustomerRecipientsFrame",
    "selectedEmailTemplateFrame",
    "sendLaterInputs",
    "sendNowIcon",
    "sendLaterIcon",
    "skippedCustomerCount",
    "skippedCustomers",
    "submitButton",
  ]

  chooseSendNow() {
    this.sendLaterInputsTarget.disabled = true
    this.sendLaterInputsTarget.classList.add("hidden")
    const icon = this.sendNowIconTarget?.innerHTML || ""
    this.submitButtonTarget.innerHTML = `${icon} Send Email`
  }

  chooseSendLater() {
    this.sendLaterInputsTarget.disabled = false
    this.sendLaterInputsTarget.classList.remove("hidden")
    const icon = this.sendLaterIconTarget?.innerHTML || ""
    this.submitButtonTarget.innerHTML = `${icon} Schedule Email`
  }

  connect() {
    const dialog = this.element.querySelector("dialog")

    if (!this.sendLaterInputsTarget.disabled) {
      this.chooseSendLater()
    }
  }

  disconnect() {
    const dialog = this.element.querySelector("dialog")
  }

  async fetchSelectedCustomerRecipients() {
    if (!this.selectedCustomerRecipientsFrameTarget) {
      return
    }

    if (!this.hasCompanyAdminCustomerSearchIndexOutlet) {
      return
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const formData = this.recipientsFormData()

    const existingCustomerIds = new Set()

    this.companyAdminCustomerSearchIndexOutlet.selectedCustomerIdsValue.forEach((customerId) => {
      existingCustomerIds.add(customerId)
    })

    existingCustomerIds.forEach((customerId) => {
      formData.append("customer_ids[]", customerId)
    })

    this.toggleSelectedCustomersLoading(true)

    const response = await fetch(this.selectedCustomerRecipientsFrameTarget.dataset.src, {
      body: formData,
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      method: "POST",
    })

    if (response.ok) {
      const html = await response.text()
      this.toggleSelectedCustomersLoading(false)
      Turbo.renderStreamMessage(html)
    }
  }

  // The server lists leases to choose from only for per-lease emails.
  recipientsFormData() {
    const formData = new FormData()
    const deliveryBasisField = this.element.querySelector(
      'input[name="create_email[delivery_basis]"]'
    )
    if (deliveryBasisField) formData.append("delivery_basis", deliveryBasisField.value)

    return formData
  }

  async removeCustomerRecipient(event) {
    if (!this.selectedCustomerRecipientsFrameTarget) {
      return
    }

    const customerIdToRemove = event.params.customerId

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content
    const formData = this.recipientsFormData()

    const customerIdFields = this.element.querySelectorAll(
      'input[name="create_email[customer_recipients][][customer_id]"]'
    )
    customerIdFields.forEach((field) => {
      if (field.value !== customerIdToRemove) {
        formData.append("customer_ids[]", field.value)
      }
    })

    this.toggleSelectedCustomersLoading(true)

    const response = await fetch(this.selectedCustomerRecipientsFrameTarget.dataset.src, {
      body: formData,
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "text/vnd.turbo-stream.html",
      },
      method: "POST",
    })

    if (response.ok) {
      const html = await response.text()
      this.toggleSelectedCustomersLoading(false)
      Turbo.renderStreamMessage(html)
    }
  }

  syncSelectedCustomersToOutlet() {
    if (!this.hasCompanyAdminCustomerSearchIndexOutlet) {
      return
    }

    const selectedCustomerIds = []
    const customerIdFields = this.element.querySelectorAll(
      'input[name="create_email[customer_recipients][][customer_id]"]'
    )
    customerIdFields.forEach((field) => {
      selectedCustomerIds.push(field.value)
    })

    this.companyAdminCustomerSearchIndexOutlet.selectedCustomerIdsValue = selectedCustomerIds
  }

  toggleSelectedCustomersLoading(on) {
    if (!this.hasSelectedCustomersLoadingIndicatorTarget) {
      return
    }

    if (!this.hasSelectedCustomerRecipientsFrameTarget) {
      return
    }

    if (!this.hasSubmitButtonTarget) {
      return
    }

    if (on) {
      this.selectedCustomersLoadingIndicatorTarget.classList.remove("hidden")
      this.selectedCustomerRecipientsFrameTarget.classList.add("hidden")
      this.submitButtonTarget.setAttribute("disabled", "")
    } else {
      this.selectedCustomersLoadingIndicatorTarget.classList.add("hidden")
      this.selectedCustomerRecipientsFrameTarget.classList.remove("hidden")
      this.submitButtonTarget.removeAttribute("disabled")
    }
  }

  updateLeaseCounts() {
    if (!this.hasLeaseCountTarget) {
      return
    }

    const checkedLeaseCounts = this.recipientTargets.map(
      (recipient) => recipient.querySelectorAll('input[name$="[lease_ids][]"]:checked').length
    )
    const leaseCustomerCount = checkedLeaseCounts.filter((count) => count > 0).length
    const skippedCustomerCount = checkedLeaseCounts.length - leaseCustomerCount

    this.leaseCountTarget.textContent = checkedLeaseCounts.reduce((sum, count) => sum + count, 0)
    this.leaseCustomerCountTarget.textContent = leaseCustomerCount
    this.skippedCustomerCountTarget.textContent = skippedCustomerCount
    this.skippedCustomersTarget.classList.toggle("hidden", skippedCustomerCount === 0)
  }

  updateSelectedEmailTemplateFrame() {
    if (!this.hasSelectedEmailTemplateFrameTarget) {
      return
    }

    if (!this.hasCustomTemplateSelectTarget) {
      return
    }

    const frame = this.selectedEmailTemplateFrameTarget
    const templateId = this.customTemplateSelectTarget.value
    const url = new URL(frame.dataset.src, window.location.origin)

    url.searchParams.set("custom_email_template_id", templateId)

    this.deliveryBasisRadioTargets.forEach((radio) => {
      if (radio.checked) {
        url.searchParams.set("delivery_basis", radio.value)
      }
    })

    frame.setAttribute("src", url.toString())
  }
}
